"use client";

import {
  type AnchorHTMLAttributes,
  type ComponentType,
  type CSSProperties,
  createContext,
  type ReactNode,
  useContext,
  useMemo,
} from "react";

/**
 * A fronteira entre o Design System e o framework do consumidor.
 *
 * O pacote é publicado para microfrontends que não são Next.js, então nenhum
 * componente pode importar `next/link`, `next/image` ou `next/navigation`
 * direto. O que eles precisam do framework — navegar, resolver uma rota,
 * otimizar uma imagem — entra por aqui.
 *
 * Os defaults são DOM puro e funcionam sozinhos: quem não passa nada recebe
 * `<a>` e `<img>` e um `navigate` que troca a URL. Um app Next injeta as
 * versões dele e ganha prefetch e otimização de imagem de volta:
 *
 * ```tsx
 * <DesignSystemProvider adapters={{ Link: NextLink, Image: NextImage }}>
 * ```
 */

export interface DSLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
  /** Dica de prefetch para roteadores que a tenham; o `<a>` padrão ignora. */
  prefetch?: boolean;
}

export type LinkComponent = ComponentType<DSLinkProps>;

export interface DSImageProps {
  src: string;
  alt: string;
  className?: string;
  style?: CSSProperties;
  /** Preenche o ancestral posicionado, como o `fill` do `next/image`. */
  fill?: boolean;
  width?: number;
  height?: number;
  sizes?: string;
  priority?: boolean;
}

export type ImageComponent = ComponentType<DSImageProps>;

export interface DesignSystemAdapters {
  /** Padrão: `<a>`. */
  Link?: LinkComponent;
  /** Padrão: `<img>` com o mesmo layout que o `fill` produz. */
  Image?: ImageComponent;
  /** Padrão: `window.location.assign`. */
  navigate?: (href: string) => void;
  /** O caminho atual, para marcar o item ativo de navegação. Padrão: `location.pathname`. */
  currentPath?: string;
  /** A query string atual, sem o `?`. Padrão: `location.search`. */
  currentSearch?: string;
}

function PlainLink({ href, children, prefetch: _prefetch, ...props }: DSLinkProps) {
  return (
    <a href={href} {...props}>
      {children}
    </a>
  );
}

/**
 * O fallback de imagem.
 *
 * `fill` precisa reproduzir o que o `next/image` gera — posição absoluta
 * cobrindo o ancestral —, senão toda foto que hoje usa `fill` colapsa para a
 * altura intrínseca e quebra sete composições. O `object-cover` continua vindo
 * do `className` de quem chama, como já vem hoje.
 *
 * `sizes` é atributo válido de `<img>` e passa direto. `priority` vira o par
 * `loading`/`fetchPriority`, que é o efeito observável dele sem o otimizador.
 */
function PlainImage({
  src,
  alt,
  className,
  style,
  fill,
  width,
  height,
  sizes,
  priority,
}: DSImageProps) {
  const fillStyle: CSSProperties | undefined = fill
    ? { position: "absolute", inset: 0, width: "100%", height: "100%", ...style }
    : style;

  return (
    // biome-ignore lint/performance/noImgElement: é justamente o fallback sem Next.
    <img
      src={src}
      alt={alt}
      className={className}
      style={fillStyle}
      width={fill ? undefined : width}
      height={fill ? undefined : height}
      sizes={sizes}
      decoding="async"
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : undefined}
    />
  );
}

function defaultNavigate(href: string) {
  if (typeof window !== "undefined") window.location.assign(href);
}

type ResolvedAdapters = Required<DesignSystemAdapters>;

const DEFAULTS: ResolvedAdapters = {
  Link: PlainLink,
  Image: PlainImage,
  navigate: defaultNavigate,
  currentPath: "",
  currentSearch: "",
};

const AdaptersContext = createContext<DesignSystemAdapters | null>(null);

export interface DesignSystemProviderProps {
  adapters?: DesignSystemAdapters;
  children: ReactNode;
}

export function DesignSystemProvider({ adapters, children }: DesignSystemProviderProps) {
  const value = useMemo(() => adapters ?? {}, [adapters]);
  return <AdaptersContext.Provider value={value}>{children}</AdaptersContext.Provider>;
}

/**
 * Sem Provider devolve os defaults, de propósito: um `<Button>` ou um `<Link>`
 * solto num teste ou numa story não deve exigir setup.
 */
export function useAdapters(): ResolvedAdapters {
  const ctx = useContext(AdaptersContext);
  return {
    Link: ctx?.Link ?? DEFAULTS.Link,
    Image: ctx?.Image ?? DEFAULTS.Image,
    navigate: ctx?.navigate ?? DEFAULTS.navigate,
    currentPath:
      ctx?.currentPath ??
      (typeof window === "undefined" ? DEFAULTS.currentPath : window.location.pathname),
    currentSearch:
      ctx?.currentSearch ??
      (typeof window === "undefined"
        ? DEFAULTS.currentSearch
        : window.location.search.replace(/^\?/, "")),
  };
}
